import axios from 'axios'
import React, { useEffect, useRef, useState } from 'react'
import { Box, ButtonBase, CircularProgress, Divider, IconButton, Modal, Paper, Typography } from '@mui/material'
import features from '../../data/features.json'
import ReactHTMLParser from 'react-html-parser';
import CloseIcon from '@mui/icons-material/Close';
import InfoIcon from '@mui/icons-material/Info';

const FeaturesListWithHelpDocs = (props) => {

  const [ activeFeature, setActiveFeature ] = useState(null);
  const [ activeTutorialURI, setActiveTutorialURI ] = useState("");
  const [ activeTutorialHTML, setActiveTutorialHTML ] = useState(null);
  const [ featuresToRender, setFeaturesToRender ] = useState([]);
  const [ isTutorialLoading, setIsTutorialLoading ] = useState(false);
  const [ isModalOpen, setIsModalOpen ] = useState(false);
  const renderedHTMLRef = useRef(null);
  const helpURL = 'https://cadsoftcorporation1.mcoutput.com/personalarchhelp/Content/';

  const handleModalClose = () => {
    setIsModalOpen(false);
    setActiveFeature(null);
    setActiveTutorialURI("");
    setActiveTutorialHTML(null);
  }
  const handleModalOpen = (feature, uri) => {
    setActiveFeature(feature);
    setActiveTutorialURI(uri);
    setIsModalOpen(true);
  }

  // switches through and renders pro and non-pro features differently.
  useEffect(() => {
    switch (props?.isPro) {
      case undefined:
        setFeaturesToRender(features);
        break;
      case true:
        const proFeatures = features.filter((feature) => feature.isPro === true);
        setFeaturesToRender(proFeatures);
        break;
      case false:
        const basicFeatures = features.filter((feature) => feature.isPro === false);
        setFeaturesToRender(basicFeatures);
        break;
      default:
        setFeaturesToRender(features);
        break;
    }
    
    return () => {}

  }, [props?.isPro]);

  // fetches active help doc.
  useEffect(() => {
    if (activeTutorialURI) {
      setIsTutorialLoading(true);
      axios.get(activeTutorialURI)
        .then(res => res.data)
        .then(data => {
          const tempContainer = document.createElement('div');
          tempContainer.innerHTML = data;
          const bodyContainer = tempContainer.querySelector('.body-container');
          if (bodyContainer) {
            const bodyContent = bodyContainer.innerHTML;
            const editedBodyContent = bodyContent.replace(/src="/g, `src="${helpURL}`);
            setActiveTutorialHTML(editedBodyContent);
            setIsTutorialLoading(false);
          }
        })
        .catch(error => { console.error(error) })
        .finally(() => {});
    }
    
    return () => {}
  }, [activeTutorialURI]);

  const handleTutorialLinkClick = (url) => {
    const activeURL = helpURL + url;
    setActiveTutorialURI(activeURL);
  };

  useEffect(() => {
    if (renderedHTMLRef.current) {
      const buttons = renderedHTMLRef.current.querySelectorAll('button');
      buttons.forEach(button => {
        button.addEventListener('click', () => {
          handleTutorialLinkClick(button.getAttribute('data-href'));
        });
      });
      
      return () => {
        buttons.forEach(button => {
          button.removeEventListener('click', () => {
            handleTutorialLinkClick(button.getAttribute('data-href'));
          });
        });
      };
    }
  }, [activeTutorialHTML]);

  const modifiedHTML = activeTutorialHTML?.replace(/<a\b[^>]*href=['"](.*?)['"][^>]*>(.*?)<\/a>/g, (match, href, content) => {
    return `<button style="background-color: transparent; border: 1px solid #b71c1c; border-radius: 4px; cursor: pointer;" data-href="${href}">${content}</button>`;
  });
  
  return (
    <Box>
      <Box>
        {
          featuresToRender.map(feature => (
            <Box key={feature?.id}>
              <Paper sx={{alignItems: "center", display: "flex", my:1, p:1, width: "100%",}}>
                <ButtonBase disabled={feature.tutorial_uri === "" ? true : false} onClick={() => handleModalOpen(feature, feature?.tutorial_uri)} sx={{borderRadius: 2, justifyContent: "flex-start", width: "100%"}}>
                  <Paper
                    component="div"
                    sx={{
                      alignItems: "center",
                      backgroundColor: feature?.background_color,
                      display: "flex",
                      justifyContent: "center",
                      height: 60, width: 60,
                      maxHeight: 60, maxWidth: 60,
                      minHeight: 60, minWidth: 60,
                    }}
                  >
                    <Box
                      component="img"
                      height={24}
                      src={
                        process.env.NODE_ENV === 'development' ? 
                        "/assets/svg/icons/white/" + feature?.slug + ".svg" :
                        "/wp-content/themes/personal-architect/assets/svg/icons/white/" + feature?.slug + ".svg"
                      }
                      width={24}
                    ></Box>
                  </Paper>
                  <Box sx={{ml:2,}}>
                    <Box sx={{alignItems: "center", display: "flex",}}>
                      <Typography component="h2" fontWeight={600} variant="h6">{feature?.name}</Typography>
                      { feature?.isPro === true ? (
                        <Box sx={{ml:1,}}>
                          <Typography sx={{ color: "#fff", backgroundColor: "#b71c1c", borderRadius: "4px", pl: .8, pr: .8,}}>Pro</Typography>
                        </Box>
                      ) : null }
                      {
                        feature?.tutorial_uri !== "" ? (
                          <Box sx={{display: "flex", alignItems: "center", flex: 1, ml:1,}}>
                            <InfoIcon fontSize='small' sx={{color: "#aaa"}}/>
                          </Box>
                        ) : null
                      }
                    </Box>
                    <Typography>{feature?.short_description}</Typography>
                  </Box>
                </ButtonBase>
              </Paper>
            </Box>
          ))
        }
      </Box>
      <Modal open={isModalOpen} onClose={handleModalClose}>
        <Box sx={{alignItems: "center", display: "flex", justifyContent: "center",}}>
          <Paper sx={{display: 'flex', flex: 3, flexDirection: "column", minWidth: 350, m: 1, p: 2}}>
            <Box sx={{display: "flex",}}>
              <Box sx={{alignItems: "center", display: "flex", flex: 1,}}>
                <Paper
                  component="div"
                  sx={{
                    alignItems: "center",
                    backgroundColor: activeFeature?.background_color,
                    display: "flex",
                    justifyContent: "center",
                    height: 40, width: 40,
                    maxHeight: 40, maxWidth: 40,
                    minHeight: 40, minWidth: 40,
                  }}
                >
                  <Box
                    component="img"
                    height={24}
                    src={
                      process.env.NODE_ENV === 'development' ? 
                      "/assets/svg/icons/white/" + activeFeature?.slug + ".svg" :
                      "/wp-content/themes/personal-architect/assets/svg/icons/white/" + activeFeature?.slug + ".svg"
                    }
                    width={24}
                  ></Box>
                </Paper>
                <Box sx={{ml:2,}}>
                  <Box sx={{alignItems: "center", display: "flex",}}>
                    <Typography component="h2" fontWeight={600} variant="h6">{activeFeature?.name}</Typography>
                    { activeFeature?.isPro === true ? (
                      <Box sx={{ml:1,}}>
                        <Typography sx={{ color: "#fff", backgroundColor: "#b71c1c", borderRadius: "4px", pl: .8, pr: .8,}}>Pro</Typography>
                      </Box>
                    ) : null }
                  </Box>
                </Box>
              </Box>
              <Box sx={{display: "flex", justifyContent: "flex-end", alignItems: "center",}}>
                <IconButton onClick={() => handleModalClose()}>
                  <CloseIcon />
                </IconButton>
              </Box>
            </Box>
            <Divider sx={{ mt:2, }}/>
            <Box sx={{flex: 1, maxHeight: '72.5vh', overflowY: 'scroll'}}>
              {/* Displays activeTutorialURI for debugging. */}
              {/* <Typography>activeTutorialURI: {activeTutorialURI}</Typography> */}
              <Box sx={{display: 'flex', flex: 1}}>
                { isTutorialLoading ? (
                  <Box sx={{alignItems: 'center', display: 'flex', flex: 1, m:1, justifyContent: 'center'}}>
                    <CircularProgress />
                  </Box>
                ) : (
                  <Box>
                    { modifiedHTML ? (
                      <Box className="renderedHTML" ref={renderedHTMLRef}>
                        { ReactHTMLParser(modifiedHTML, {
                            decodeEntities: true,
                            transform: (node, index) => {
                              // Handle any additional transformations if needed
                              return undefined;
                            }
                          })
                        }
                      </Box>
                    ) : (
                      <Typography>No tutorial content available.</Typography>
                    )}
                  </Box>
                )}
              </Box>
            </Box>
          </Paper>
        </Box>
      </Modal>
    </Box>
  )
}

export default FeaturesListWithHelpDocs